import React from 'react';
import block from 'bem-cn-lite';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { PropTypes } from 'prop-types';

import { Button } from '../../common/components';
import { getPaletteColorClass } from '../../../base/utils';
import { reducer, actions, isRouteFilterEnabled } from '../actions';
import { isFilterActive } from '../filterUtils';
import I18N from '../i18n';

const FILTER_ICON = 'Common_Filter';
const DROPDOWN_GAP = 8;
const DROPDOWN_BOTTOM_MARGIN = 16;
const DROPDOWN_MIN_HEIGHT = 240;

const b = block('RouteFilterPanel');

// Header widget for multi-day manifests: lets the user narrow the map down
// to a subset of days and/or couriers. Selection lives in the `routeFilter`
// reducer; LeafletMap applies it when rendering routes and points.
class RouteFilterPanel extends React.PureComponent {
  static propTypes = {
    manifestId: PropTypes.number.isRequired,
    days: PropTypes.arrayOf(PropTypes.number).isRequired,
    courierIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    availableDays: PropTypes.arrayOf(PropTypes.number).isRequired,
    availableCouriers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })).isRequired,
    filterManifestId: PropTypes.number,
    enabled: PropTypes.bool,
    filterActions: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { open: false, maxHeight: null };
    this.rootRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this._onDocumentMouseDown);
    window.addEventListener('resize', this._updateMaxHeight);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this._onDocumentMouseDown);
    window.removeEventListener('resize', this._updateMaxHeight);
  }

  // The header wraps on narrow screens, so the drop-down's top offset is not
  // known statically; size it to the space left below the button.
  _updateMaxHeight = () => {
    if (!this.state.open || !this.rootRef.current)
      return;
    const bottom = this.rootRef.current.getBoundingClientRect().bottom;
    const available = window.innerHeight - bottom - DROPDOWN_GAP - DROPDOWN_BOTTOM_MARGIN;
    this.setState({ maxHeight: Math.max(available, DROPDOWN_MIN_HEIGHT) });
  }

  _onDocumentMouseDown = (e) => {
    if (this.state.open && this.rootRef.current && !this.rootRef.current.contains(e.target))
      this.setState({ open: false });
  }

  _toggleOpen = () => this.setState((state) => ({ open: !state.open }), this._updateMaxHeight);

  _isActive = () => isFilterActive({ days: this.props.days, courierIds: this.props.courierIds });

  _selectedCount = () => this.props.days.length + this.props.courierIds.length;

  _renderDays() {
    const { availableDays, days, filterActions } = this.props;
    if (availableDays.length < 2)
      return null;

    return <div className={b('Section')}>
      <div className={b('SectionHeader')}>
        <span className={b('SectionTitle')}>{I18N.DAYS}</span>
        <span className={b('SectionCounter')}>
          {days.length ? `${days.length} ${I18N.SELECTED}` : I18N.ALL}
        </span>
        <span className={b('SectionLink')} onClick={() => filterActions.setDays([])}>{I18N.ALL}</span>
      </div>
      <div className={b('Chips')}>
        {availableDays.map((day) => <div
          key={day}
          className={b('Chip', { 'Active': days.includes(day) })}
          onClick={() => filterActions.toggleDay(day)}
        >
          <span className={b('ChipLabel')}>{I18N.DAY}</span>
          <span className={b('ChipValue')}>{day}</span>
        </div>)}
      </div>
    </div>;
  }

  _renderCouriers() {
    const { availableCouriers, courierIds, filterActions } = this.props;
    if (!availableCouriers.length)
      return null;

    return <div className={b('Section')}>
      <div className={b('SectionHeader')}>
        <span className={b('SectionTitle')}>{I18N.COURIERS}</span>
        <span className={b('SectionCounter')}>
          {courierIds.length ? `${courierIds.length} ${I18N.SELECTED}` : I18N.ALL}
        </span>
        <span className={b('SectionLink')} onClick={() => filterActions.setCouriers([])}>{I18N.ALL}</span>
      </div>
      <div className={b('Couriers')}>
        {availableCouriers.map((c) => <div
          key={c.id}
          className={b('Courier', { 'Active': courierIds.includes(c.id) })}
          onClick={() => filterActions.toggleCourier(c.id)}
        >
          <span className={`${b('CourierColor')} ${getPaletteColorClass(c.id)}`}/>
          <span className={b('CourierName')}>{c.name}</span>
          <span className={b('CourierCheck')}/>
        </div>)}
      </div>
    </div>;
  }

  _renderDropDown() {
    const hasOptions = this.props.availableDays.length > 0 || this.props.availableCouriers.length > 0;

    return <div className={b('DropDown')} style={{ maxHeight: this.state.maxHeight || undefined }}>
      <div className={b('Title')}>{I18N.TITLE}</div>
      {hasOptions
        ? <div className={b('Body')}>
          {this._renderDays()}
          {this._renderCouriers()}
        </div>
        : <div className={b('Empty')}>{I18N.EMPTY}</div>}
      <div className={b('Footer')}>
        <Button
          disabled={!this._isActive()}
          onClick={this.props.filterActions.reset}
        >
          {I18N.RESET}
        </Button>
      </div>
    </div>;
  }

  render() {
    if (!this.props.enabled)
      return null;
    // Options belong to a previously opened manifest until LeafletMap
    // re-collects them for the current one; do not show stale lists.
    if (this.props.filterManifestId !== this.props.manifestId)
      return null;
    if (!this.props.availableCouriers.length)
      return null;

    const active = this._isActive();

    return <div className={b({ 'Open': this.state.open })} ref={this.rootRef}>
      <div className={b('Block', { 'Active': active })}>
        <Button
          toggledOff={!active && !this.state.open}
          onClick={this._toggleOpen}
          className={b('Button')}
          leftHint={this.state.open ? '' : I18N.HINT}
          icon={FILTER_ICON}
        />
        {active && <div className={b('Badge')}>{this._selectedCount()}</div>}
      </div>
      {this.state.open && this._renderDropDown()}
    </div>;
  }
}

export default
connect(
  (state) => ({
    days: state[reducer.name].days,
    courierIds: state[reducer.name].courierIds,
    availableDays: state[reducer.name].availableDays,
    availableCouriers: state[reducer.name].availableCouriers,
    filterManifestId: state[reducer.name].manifestId,
    enabled: isRouteFilterEnabled(state),
  }),
  (dispatch) => ({
    filterActions: bindActionCreators(actions, dispatch),
  })
)(RouteFilterPanel);
